import { useEffect, useRef, useState } from 'react';
import {
  CAPTURE,
  TAG_GROUPS,
  VOCABULARY_CONFIRMED,
  VOCABULARY_NOTICE,
  uuidv7,
  nowIso,
  type Media,
} from '@kilroy/shared';
import { writeMedia } from '../../media/opfs';
import { VoiceRecorder, extensionFor, formatDuration } from '../../media/recorder';
import { useMediaUrl } from '../../hooks/useMediaUrl';
import { IconCamera, IconMic } from '../icons';

/* ── Photo well ───────────────────────────────────────────────────────────
 *
 * Native capture via `input capture` rather than getUserMedia: it hands back
 * the camera app's original JPEG with EXIF intact, at full sensor resolution,
 * and it works when the browser's own camera pipeline doesn't. The bytes are
 * written to OPFS unmodified — no client re-encode, ever. A photo is evidence,
 * and re-encoding evidence is how you lose an argument three years later.
 *
 * Retaking supersedes. It never deletes.
 */

export function PhotoWell({
  media,
  onChange,
  fix,
}: {
  media: Media[];
  onChange: (next: Media[]) => void;
  /** Live fix, stamped onto each file as the shutter fires. */
  fix?: { lat: number; lon: number; accuracy: number } | null;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [supersedeId, setSupersedeId] = useState<string | null>(null);

  const photos = media.filter((m) => m.kind === 'photo' && !m.superseded_by);

  async function onFiles(list: FileList | null) {
    if (!list?.length) return;
    const added: Media[] = [];

    for (const file of Array.from(list)) {
      const id = uuidv7();
      const name = `${id}.${(file.type.split('/')[1] ?? 'jpg').replace('jpeg', 'jpg')}`;
      await writeMedia(name, file);
      added.push({
        id,
        kind: 'photo',
        mime: file.type || 'image/jpeg',
        bytes: file.size,
        file: name,
        captured_at: nowIso(),
        ...(fix ? { lat: fix.lat, lon: fix.lon, accuracy_m: fix.accuracy } : {}),
      });
    }

    if (supersedeId && added[0]) {
      // The original stays in the record with a pointer to what replaced it.
      onChange([
        ...media.map((m) =>
          m.id === supersedeId ? { ...m, superseded_by: added[0].id } : m,
        ),
        ...added,
      ]);
      setSupersedeId(null);
    } else {
      onChange([...media, ...added]);
    }
    if (input.current) input.current.value = '';
  }

  return (
    <div className="field">
      <div className="label">Photos</div>
      <div className="photowell">
        {photos.map((m, i) => (
          <PhotoTile
            key={m.id}
            media={m}
            cover={i === 0}
            onRetake={() => {
              setSupersedeId(m.id);
              input.current?.click();
            }}
          />
        ))}

        <button
          type="button"
          className="photowell__add"
          onClick={() => {
            setSupersedeId(null);
            input.current?.click();
          }}
        >
          <IconCamera size={26} />
          <span>{photos.length ? 'Add' : CAPTURE.photoEmpty}</span>
        </button>
      </div>
      {photos.length > 0 && <p className="field__hint">{CAPTURE.retakeNote}</p>}

      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        hidden
        onChange={(e) => void onFiles(e.target.files)}
      />
    </div>
  );
}

function PhotoTile({
  media,
  cover,
  onRetake,
}: {
  media: Media;
  cover: boolean;
  onRetake: () => void;
}) {
  const url = useMediaUrl(media.file);
  return (
    <div className="phototile">
      {url && <img src={url} alt="" />}
      {cover && <span className="phototile__cover mono">{CAPTURE.photoCover}</span>}
      <button type="button" className="phototile__retake" onClick={onRetake}>
        Retake
      </button>
    </div>
  );
}

/* ── Voice notes ──────────────────────────────────────────────────────────
 *
 * "Field captured observations including GPS, Photo, and spoken notes" is the
 * one thing Brandon said must never stop working. So: tap to start, tap to
 * stop (not press-and-hold, which fails the moment your glove slips), multiple
 * notes per record, a live level meter so you can see it's listening, and a
 * wake lock so the screen dimming mid-sentence doesn't end the recording.
 */

export function VoiceNotes({
  media,
  onChange,
  fix,
}: {
  media: Media[];
  onChange: (next: Media[]) => void;
  fix?: { lat: number; lon: number; accuracy: number } | null;
}) {
  const [rec] = useState(() => new VoiceRecorder());
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const notes = media.filter((m) => m.kind === 'audio');

  useEffect(() => {
    if (!recording) return;
    const t = setInterval(() => setElapsed(rec.elapsed), 200);
    return () => clearInterval(t);
  }, [recording, rec]);

  useEffect(() => () => rec.cancel(), [rec]);

  async function toggle() {
    if (recording) {
      const result = await rec.stop();
      setRecording(false);
      setLevel(0);
      setElapsed(0);
      if (!result) return;

      const id = uuidv7();
      const name = `${id}.${extensionFor(result.mime)}`;
      await writeMedia(name, result.blob);
      onChange([
        ...media,
        {
          id,
          kind: 'audio',
          mime: result.mime,
          bytes: result.blob.size,
          file: name,
          captured_at: nowIso(),
          duration: result.duration,
          ...(fix ? { lat: fix.lat, lon: fix.lon, accuracy_m: fix.accuracy } : {}),
        },
      ]);
      return;
    }

    try {
      rec.onLevel = setLevel;
      await rec.start();
      setRecording(true);
      setError(null);
    } catch {
      setError('Microphone unavailable. The record still saves without a note.');
    }
  }

  return (
    <div className="field">
      <div className="label">Voice note</div>

      {notes.map((m, i) => (
        <div key={m.id} className="voicenote">
          <IconMic size={18} />
          <span className="grow mono">
            Note {i + 1} · {formatDuration(m.duration ?? 0)}
          </span>
          <span className="mono voicenote__pending">transcribes on sync</span>
        </div>
      ))}

      <button
        type="button"
        className={`recbtn${recording ? ' recbtn--live' : ''}`}
        onClick={() => void toggle()}
      >
        <span className="recbtn__icon" aria-hidden="true">
          {recording ? <span className="recbtn__stop" /> : <IconMic size={24} />}
        </span>
        <span className="grow" style={{ textAlign: 'left' }}>
          {recording ? CAPTURE.voiceRecording : notes.length ? 'Add another note' : 'Record a note'}
        </span>
        {recording ? (
          <span className="mono recbtn__elapsed">{formatDuration(elapsed)}</span>
        ) : null}
      </button>

      {recording && (
        <div className="wave" aria-hidden="true">
          {Array.from({ length: 28 }, (_, i) => (
            <span
              key={i}
              className="wave__bar"
              style={{
                // Deterministic shape modulated by live input, so the meter
                // reads as an instrument rather than a decoration that would
                // animate identically with the mic muted.
                height: `${18 + Math.abs(Math.sin(i * 0.7)) * level * 82}%`,
              }}
            />
          ))}
        </div>
      )}

      <p className="field__hint">{error ?? CAPTURE.voiceHint}</p>
    </div>
  );
}

/* ── Tags ─────────────────────────────────────────────────────────────────
 *
 * The vocabulary is not confirmed by the geology team, and the screen says so
 * rather than presenting a guess as a standard. When the list is signed off,
 * flip VOCABULARY_CONFIRMED and this notice disappears on both surfaces.
 */

export function TagPicker({
  selected,
  onChange,
  groups = TAG_GROUPS.map((g) => g.id),
}: {
  selected: string[];
  onChange: (next: string[]) => void;
  groups?: string[];
}) {
  const toggle = (tag: string) =>
    onChange(
      selected.includes(tag) ? selected.filter((t) => t !== tag) : [...selected, tag],
    );

  return (
    <div className="field">
      {TAG_GROUPS.filter((g) => groups.includes(g.id)).map((group) => (
        <div key={group.id} className="taggroup">
          <div className="label">{group.label}</div>
          <div className="chiprow">
            {group.tags.map((tag) => (
              <button
                key={tag}
                type="button"
                className="chip"
                aria-pressed={selected.includes(tag)}
                onClick={() => toggle(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
          {group.provenance === 'placeholder' && (
            <p className="field__hint">{group.note}</p>
          )}
        </div>
      ))}

      {!VOCABULARY_CONFIRMED && <p className="notice">{VOCABULARY_NOTICE}</p>}
    </div>
  );
}
